import Card from '../../components/Card';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useT } from '../../utils/useTranslation';
import api, { DB_TYPE } from '../../api';
import { db } from '../../firebase';
import { getDocs, collection, query, limit, orderBy, getCountFromServer } from 'firebase/firestore';
import { motion } from 'framer-motion';
import heroBg from '../../assets/sas_hero_new.png';
import ajrakTexture from '../../assets/ajrak_texture_new.jpg';
import HeroBackground from '../../components/ui/HeroBackground';
const AjrakDiamond = ({
  size = 40
}) => <motion.div animate={{
  rotate: [45, 55, 45],
  scale: [1, 1.1, 1],
  opacity: [0.6, 1, 0.6]
}} transition={{
  duration: 4,
  repeat: Infinity,
  ease: "easeInOut"
}} style={{
  display: 'inline-flex'
}}>
    <svg width={size} height={size} viewBox="0 0 32 32">
      <defs>
        <linearGradient id="goldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="var(--sindhi-gold)" />
          <stop offset="100%" stopColor="#FFF" />
        </linearGradient>
      </defs>
      <rect x="8" y="8" width="16" height="16" transform="rotate(45 16 16)" fill="url(#goldGrad)" />
      <rect x="11" y="11" width="10" height="10" transform="rotate(45 16 16)" fill="var(--sindhi-maroon)" />
      <circle cx="16" cy="16" r="3" fill="white" />
    </svg>
  </motion.div>;
const StatBadge = ({
  num,
  label,
  color
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true
}} whileHover={{
  y: -5,
  scale: 1.05
}} style={{
  textAlign: 'center',
  padding: '20px 30px',
  background: 'rgba(255,255,255,0.08)',
  borderRadius: '24px',
  border: '1px solid rgba(255,255,255,0.2)',
  backdropFilter: 'blur(20px)',
  minWidth: 150,
  boxShadow: '0 20px 40px -10px rgba(0,0,0,0.2)'
}}>
    <div style={{
      fontSize: 36,
      fontWeight: 800,
      fontFamily: 'var(--font-display)',
      color: color || 'white',
      textShadow: '0 10px 20px rgba(0,0,0,0.2)'
    }}>{num}</div>
    <div style={{
      fontSize: 12,
      color: 'rgba(255,255,255,0.8)',
      marginTop: 4,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: 1.2
    }}>{label}</div>
  </motion.div>;
const FeatureCard = ({
  icon,
  title,
  desc,
  to,
  index
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true
}} whileHover={{
  y: -12,
  scale: 1.02
}} transition={{
  delay: index * 0.1,
  type: 'spring',
  stiffness: 300,
  damping: 20
}}>
    <Link to={to} style={{
      textDecoration: 'none'
    }}>
      <div className="card-royal" style={{
        padding: '48px 32px',
        height: '100%',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center'
      }}>
        <div style={{
          width: 80,
          height: 80,
          borderRadius: 24,
          marginBottom: 24,
          background: 'var(--sindhi-ivory)',
          border: '1px solid var(--sindhi-gold-muted)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 32,
          boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05)'
        }}>{icon}</div>
        <h3 style={{
          fontFamily: 'var(--font-display)',
          fontSize: 22,
          fontWeight: 800,
          color: 'var(--sindhi-navy)',
          marginBottom: 12
        }}>{title}</h3>
        <p style={{
          fontSize: 15,
          color: 'var(--sindhi-text-muted)',
          lineHeight: 1.6,
          marginBottom: 20
        }}>{desc}</p>
        <div style={{
          marginTop: 'auto',
          color: 'var(--sindhi-maroon)',
          fontWeight: 800,
          fontSize: 13,
          textTransform: 'uppercase',
          letterSpacing: 1
        }}>
          Learn More →
        </div>
      </div>
    </Link>
  </motion.div>;
export default function HomePage() {
  const { user } = useAuth();
  const t = useT();
  const [news, setNews] = useState([]);
  const [stats, setStats] = useState({
    branches: 0,
    books: 0,
    members: 0
  });

  useEffect(() => {
    if (DB_TYPE === 'firebase') {
      // Fetch Latest News
      getDocs(query(collection(db, 'events'), limit(3), orderBy('createdAt', 'desc')))
        .then(snap => {
          setNews(snap.docs.map(d => ({ _id: d.id, ...d.data() })));
        }).catch(() => { });

      // Fetch Stats
      Promise.all([
        getCountFromServer(collection(db, 'branches')),
        getCountFromServer(collection(db, 'books'))
      ]).then(([bCount, bkCount]) => {
        const bc = bCount.data().count;
        const bkc = bkCount.data().count;
        setStats({
          branches: bc,
          books: bkc,
          members: bc * 12 // Heuristic
        });
      }).catch(() => { });
    } else {
      api.get('/news').then(r => setNews(r.data.slice(0, 3))).catch(() => { });
      Promise.all([api.get('/branches').then(r => r.data.length), api.get('/books').then(r => r.data.length)]).then(([branches, books]) => setStats({
        branches,
        books,
        members: branches * 12
      })).catch(() => { });
    }
  }, []);

  const isRTL = t.language === 'sindhi';

  return <div style={{
    overflowX: 'hidden',
    direction: isRTL ? 'rtl' : 'ltr'
  }}>
    {/* ── HERO ── */}
    <section className="hero-premium" style={{
      minHeight: 'calc(100vh - 80px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '100px 2rem 160px',
      position: 'sticky',
      top: 0,
      zIndex: 1
    }}>
      <HeroBackground />

      <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: "easeOut" }} className="hero-content">
        <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginBottom: 32 }}>
          <AjrakDiamond /><AjrakDiamond /><AjrakDiamond />
        </div>

        <div style={{
          fontSize: 16,
          letterSpacing: 2,
          color: 'var(--sindhi-gold)',
          marginBottom: 24,
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          flexDirection: isRTL ? 'row-reverse' : 'row'
        }}>
          <span style={{ letterSpacing: 4, textTransform: 'uppercase', fontSize: 13, fontWeight: 800 }}>{isRTL ? 'قائم ٿيو 1958' : 'Established 1958'}</span>
          <span style={{ opacity: 0.3 }}>|</span>
          <span style={{ fontFamily: 'var(--font-sindhi)', fontSize: 28 }}>سنڌي ادبي سنگت سنڌ</span>
        </div>
        <h1 className="hero-title text-gradient-gold" style={{
          background: 'none',
          WebkitTextFillColor: 'initial',
          fontFamily: isRTL ? 'var(--font-sindhi)' : 'var(--font-display)',
          fontSize: isRTL ? 72 : 56
        }}>{t('welcomeTitle')}</h1>
        <p className="hero-sub" style={{
          color: 'rgba(255,255,255,0.95)',
          fontWeight: 500,
          fontSize: isRTL ? 24 : 20,
          fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit',
          maxWidth: 800,
          margin: '0 auto'
        }}>
          {t('welcomeDesc')}
        </p>
        {/* Stats */}
        <div style={{ display: 'flex', gap: 20, justifyContent: 'center', flexWrap: 'wrap', marginTop: 60 }}>
          <StatBadge num={`${stats.branches}+`} label={t('activeBranches')} color="var(--sindhi-gold)" />
          <StatBadge num={`${stats.books}+`} label={t('books_count')} color="var(--sindhi-gold)" />
          <StatBadge num="60+" label={t('yearsLegacy')} color="var(--sindhi-gold)" />
        </div>
      </motion.div>

      {/* Bottom decorative mask */}
      <div style={{ position: 'absolute', bottom: -1, left: 0, right: 0, zIndex: 10 }}>
        <svg viewBox="0 0 1440 120" preserveAspectRatio="none" style={{ display: 'block', width: '100%', height: 80 }}>
          <path d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64" fill="none" stroke="var(--sindhi-gold)" strokeWidth="4" opacity="0.8" />
          <path d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z" fill="var(--sindhi-ivory)" />
        </svg>
      </div>
    </section>

    {/* ── FEATURES ── */}
    <section style={{ padding: '120px 2rem', position: 'relative', background: 'var(--sindhi-ivory)', zIndex: 10 }}>
      <div className="ajrak-pattern-bg" style={{ position: 'absolute', inset: 0, opacity: 0.5 }} />
      <div style={{ maxWidth: 1200, margin: '0 auto', position: 'relative', zIndex: 1 }}>
        <div style={{ textAlign: 'center', marginBottom: 64 }}>
          <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="section-title text-gradient-gold" style={{
            background: 'linear-gradient(135deg, var(--sindhi-gold), #7D5A0A)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontFamily: isRTL ? 'var(--font-sindhi)' : 'var(--font-display)',
            fontSize: isRTL ? 48 : 36
          }}>
            {t('explorePlatform')}
          </motion.h2>
          <p style={{
            marginTop: 20,
            color: 'var(--sindhi-text-muted)',
            fontSize: 18,
            maxWidth: 600,
            margin: '20px auto 0',
            fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit'
          }}>
            {t('platformDesc')}
          </p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 24, direction: isRTL ? 'rtl' : 'ltr' }}>
          <FeatureCard index={0} icon="🌿" to="/branches" title={t('branches')} desc={t('branchesDesc')} isRTL={isRTL} />
          <FeatureCard index={1} icon="📚" to="/books" title={t('books')} desc={t('booksDesc')} isRTL={isRTL} />
          <FeatureCard index={2} icon="👥" to="/board" title={t('board')} desc={t('boardDesc')} isRTL={isRTL} />
          <FeatureCard index={3} icon="🏛️" to="/ohdedaan" title={t('ohdedaran')} desc={t('ohdedaanDesc')} isRTL={isRTL} />
          <FeatureCard index={4} icon="📅" to="/events" title={t('events')} desc={t('eventsDesc')} isRTL={isRTL} />
          <FeatureCard index={5} icon="📜" to="/qanoon" title={t('qanoon')} desc={t('qanoonDesc')} isRTL={isRTL} />
        </div>
      </div>
    </section>

    {/* ── LATEST NEWS ── */}
    {news.length > 0 && <section style={{ padding: '100px 2rem', background: `url(${ajrakTexture})`, backgroundSize: '600px', backgroundAttachment: 'fixed', position: 'relative' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(253,248,235,0.85)', backdropFilter: 'blur(2px)' }} />
      <div style={{ maxWidth: 1200, margin: '0 auto', position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 60, textAlign: 'center' }}>
          <h2 className="section-title text-gradient-gold" style={{
            background: 'linear-gradient(135deg, var(--sindhi-gold), #7D5A0A)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontFamily: isRTL ? 'var(--font-sindhi)' : 'var(--font-display)',
            fontSize: isRTL ? 42 : 32
          }}>{t('latestNews')}</h2>
          <p style={{
            color: 'var(--sindhi-text-muted)',
            marginTop: 12,
            maxWidth: 600,
            fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit'
          }}>
            {isRTL ? 'سنڌي ادبي سنگت جي تازين خبرن، واقعن ۽ ادبي اعلانن سان باخبر رهو.' : 'Stay updated with the latest happenings, events, and literary announcements from SAS.'}
          </p>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 40 }}>
          <Link to="/events" className="btn btn-outline btn-sm" style={{ borderRadius: 12, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('viewAll')}</Link>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(340px,1fr))', gap: 28, direction: isRTL ? 'rtl' : 'ltr' }}>
          {news.map((item, idx) => {
            const displayTitle = isRTL ? (item.titleSindhi || item.title) : item.title;
            const displayDesc = isRTL ? (item.descriptionSindhi || item.description) : item.description;

            return <Card key={item._id} style={{ padding: 32, background: 'white', textAlign: isRTL ? 'right' : 'left' }}>
              <span className={`badge badge-${item.type === 'announcement' ? 'red' : 'navy'}`} style={{ marginBottom: 16, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
                {isRTL ? (item.type === 'announcement' ? 'اعلان' : 'تقريب') : item.type}
              </span>
              <h3 style={{
                fontFamily: isRTL ? 'var(--font-sindhi)' : 'var(--font-display)',
                fontSize: isRTL ? 22 : 18,
                fontWeight: 700,
                color: 'var(--sindhi-navy)',
                marginBottom: 12
              }}>
                {displayTitle}
              </h3>
              <p style={{
                fontSize: 15,
                color: 'var(--sindhi-text-muted)',
                lineHeight: 1.6,
                marginBottom: 20,
                fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit'
              }}>
                {displayDesc?.slice(0, 140)}...
              </p>
              <div style={{
                marginTop: 'auto',
                fontSize: 13,
                color: 'var(--sindhi-text-muted)',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexDirection: isRTL ? 'row-reverse' : 'row',
                justifyContent: isRTL ? 'flex-start' : 'flex-start'
              }}>
                <span style={{ opacity: 0.5 }}>📅</span>
                <span style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
                  {new Date(item.date || item.createdAt).toLocaleDateString(isRTL ? 'sd-PK' : 'en-PK', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>
            </Card>;
          })}
        </div>
      </div>
    </section>}

    {/* ── JOIN CTA ── */}
    <section style={{
      padding: '120px 2rem',
      textAlign: 'center',
      background: 'linear-gradient(135deg, var(--sindhi-navy), #0a192f)',
      color: 'white',
      position: 'relative',
      overflow: 'hidden',
      borderTop: '1px solid var(--sindhi-gold-muted)',
      borderBottom: '1px solid var(--sindhi-gold-muted)'
    }}>
      <div style={{
        position: 'absolute',
        inset: 0,
        opacity: 0.1,
        backgroundImage: 'radial-gradient(circle, var(--sindhi-gold) 1px, transparent 1px)',
        backgroundSize: '40px 40px'
      }} />
      <div style={{ position: 'relative', zIndex: 1, maxWidth: 800, margin: '0 auto' }}>
        <h2 className="text-gradient-gold" style={{
          fontFamily: isRTL ? 'var(--font-sindhi)' : 'var(--font-display)',
          fontSize: isRTL ? 56 : 48,
          fontWeight: 800,
          marginBottom: 24
        }}>
          {t('becomeMember')}
        </h2>
        <p style={{
          fontSize: isRTL ? 20 : 18,
          color: 'rgba(255,255,255,0.7)',
          marginBottom: 40,
          lineHeight: 1.6,
          fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit'
        }}>
          {isRTL ? 'سنڌي ٻولي ۽ ادب جي واڌاري ۽ تحفظ لاءِ وقف ڪيل سڀ کان وڏي ادبي تنظيم ۾ شامل ٿيو.' : 'Join the largest literary organization dedicated to the promotion and preservation of Sindhi language and literature.'}
        </p>
        <Link to="/register" className="btn-premium" style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
          {t('joinAsMember')}
        </Link>
      </div>
    </section>
  </div>;
}